/** @jsxImportSource ./simboluri-jsx */
// TOP SOLICITĂRI (mock-ui/design/StrEforturi.dc.html): Pasul 12 nu mai e
// livrabilul CSV afișat ca CSV — întâi răspunde la „ce e cel mai solicitat".
// Se arată MAXIMUL fiecărui element (componente concomitente, din aceeași
// combinație); restul rândurilor rămân în tabelele de mai jos și în fila
// «Eforturile lui» din pagina fiecărui element.

import { useMemo } from "react";
import { useT } from "../i18n";
import { EfortRand } from "./tipuri";
import { nr } from "./elemente";
import { hashElement } from "./legaturi";

function maxPeElement(
  randuri: EfortRand[],
  scor: (r: EfortRand) => number
): EfortRand[] {
  const best = new Map<string, EfortRand>();
  for (const r of randuri) {
    const cheie = `${r.element}`;
    const v = best.get(cheie);
    if (!v || scor(r) > scor(v)) best.set(cheie, r);
  }
  return [...best.values()].sort((a, b) => scor(b) - scor(a));
}

export default function TopSolicitari({
  rutaId,
  solutie,
  grinzi,
  stalpi,
  nrCombinatii,
}: {
  rutaId: string;
  solutie?: string | null;
  grinzi: EfortRand[];
  stalpi: EfortRand[];
  nrCombinatii: number;
}) {
  const t = useT();
  const topGrinzi = useMemo(
    () => maxPeElement(grinzi, (r) => Math.abs(r.M3_knm)).slice(0, 7),
    [grinzi]
  );
  const topStalpi = useMemo(
    () => maxPeElement(stalpi, (r) => Math.abs(r.P_kn)).slice(0, 5),
    [stalpi]
  );
  if (!topGrinzi.length && !topStalpi.length) return null;

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap", margin: "14px 0" }}>
      {topGrinzi.length > 0 && (
        <div className="ob-card" style={{ flex: "2 1 480px", minWidth: 0, overflow: "hidden" }}>
          <div style={{ padding: "11px 14px", display: "flex", gap: 8, alignItems: "center" }}>
            <b>{t("struct.top.grinzi")}</b>
            <span className="ob-acasa-sub">
              {t("struct.top.sub", {
                elemente: nr(new Set(grinzi.map((g) => g.element)).size),
                combinatii: nr(nrCombinatii),
              })}
            </span>
          </div>
          <table className="ob-sol-tabel">
            <thead>
              <tr>
                <th>{t("verificari.col_element")}</th>
                <th>{t("struct.top.nivel")}</th>
                <th style={{ textAlign: "right" }}>M₃ [kNm]</th>
                <th style={{ textAlign: "right" }}>V₂ [kN]</th>
                <th style={{ textAlign: "right" }}>N [kN]</th>
                <th>{t("struct.top.combinatia")}</th>
                <th>{t("struct.top.punctul")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {topGrinzi.map((g) => (
                <tr key={g.element}>
                  <td><span className="ob-ref">{g.element}</span></td>
                  <td style={{ fontSize: 12, color: "var(--mut)" }}>{g.nivel}</td>
                  <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
                    {nr(g.M3_knm, 1)}
                  </td>
                  <td className="sg-mono" style={{ textAlign: "right" }}>{nr(g.V2_kn, 1)}</td>
                  <td className="sg-mono" style={{ textAlign: "right" }}>{nr(g.P_kn)}</td>
                  <td className="sg-mono" style={{ fontSize: 11 }}>{g.combinatie}</td>
                  <td className="sg-mono" style={{ fontSize: 11, color: "var(--mut)" }}>{g.punct}</td>
                  <td style={{ textAlign: "right" }}>
                    <a href={hashElement(rutaId, g.element, solutie)} style={{ fontSize: 11 }}>
                      {t("struct.top.elementul")}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {topStalpi.length > 0 && (
        <div className="ob-card" style={{ flex: "1 1 300px", minWidth: 0, overflow: "hidden" }}>
          <div style={{ padding: "11px 14px" }}>
            <b>{t("struct.top.stalpi")}</b>
            <div className="ob-acasa-sub" style={{ marginTop: 2 }}>
              {t("struct.top.nota_stalpi")}
            </div>
          </div>
          <table className="ob-sol-tabel">
            <thead>
              <tr>
                <th>{t("verificari.col_element")}</th>
                <th style={{ textAlign: "right" }}>N [kN]</th>
                <th style={{ textAlign: "right" }}>M₂</th>
                <th style={{ textAlign: "right" }}>M₃</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {topStalpi.map((s) => (
                <tr key={s.element}>
                  <td><span className="ob-ref">{s.element}</span></td>
                  <td className="sg-mono" style={{ textAlign: "right", fontWeight: 600 }}>
                    {nr(Math.abs(s.P_kn))}
                  </td>
                  <td className="sg-mono" style={{ textAlign: "right" }}>{nr(s.M2_knm, 1)}</td>
                  <td className="sg-mono" style={{ textAlign: "right" }}>{nr(s.M3_knm, 1)}</td>
                  <td style={{ textAlign: "right" }}>
                    <a href={hashElement(rutaId, s.element, solutie)} style={{ fontSize: 11 }}>
                      {t("struct.top.elementul")}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
